import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Fade,
  Chip,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { isMSP } from "../../helpers/util_helper";
import { countryList, getRegionCode } from "../../utilities/countries";
import { validateAddressWithAPI } from "../../helpers/addressValidation";
import { getBillingProfileLogoUploadUrl, deleteBillingProfileLogo, getCustomerMetadata } from "../../helpers/auth_aws_helper";
import { get, getApiErrorMessage } from "../../helpers/aws_api_helper";
import { GET_BILLING_PROFILE_LOGO_VIEW } from "../../helpers/url_helper";
import {
  getBillingProfile,
  getCustomers,
  updateBillingProfile,
  clearBillingManagementMessages,
} from "../../store/billingManagement/billingManagementSlice";

const ALLOWED_LOGO_EXT = /\.(png|jpe?g)$/i;
const MAX_LOGO_FILENAME_LENGTH = 255;
const LOGO_FILENAME_REGEX = /^[a-zA-Z0-9._-]+$/;

const isValidLogoFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return false;
  if (fileName.length > MAX_LOGO_FILENAME_LENGTH) return false;
  return LOGO_FILENAME_REGEX.test(fileName);
};

const getOrdinalSuffix = (n) => {
  const d = n % 10;
  const dd = n % 100;
  if (dd >= 11 && dd <= 13) return "th";
  if (d === 1) return "st";
  if (d === 2) return "nd";
  if (d === 3) return "rd";
  return "th";
};

const formatOrdinalDay = (day) => {
  if (day == null || day === "") return null;
  const n = Number(day);
  if (isNaN(n) || n < 1 || n > 31) return String(day);
  return `${n}${getOrdinalSuffix(n)}`;
};
const LOGO_MIME_BY_EXT = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg" };
const BILLING_LOGO_WEBSOCKET_URL = process.env.REACT_APP_BILLING_LOGO_SCAN_WEBSOCKET_URL || process.env.REACT_APP_IAAC_SCAN_WEBSOCKET_URL;

const defaultProfile = () => ({
  name: "Default",
  description: "",
  invoice_date: 15,
  billing_mode: "RECURRING",
  logo_s3_uri: "",
  remit_to_name: "",
  remit_to_address_line1: "",
  remit_to_address_line2: "",
  remit_to_city: "",
  remit_to_state: "",
  remit_to_postal: "",
  remit_to_country: "",
  remit_to_email: "",
  remit_to_phone: "",
  remit_to_fax: "",
  remit_to_fein: "",
  remit_to_duns: "",
  remit_to_gst: "",
  remit_to_contract_refs: "",
  remit_to_ueid: "",
  remit_to_pst: "",
  sold_to_name: "",
  sold_to_attn: "",
  sold_to_address_line1: "",
  sold_to_address_line2: "",
  sold_to_city: "",
  sold_to_state: "",
  sold_to_postal: "",
  sold_to_country: "",
  ship_to_name: "",
  ship_to_attn: "",
  ship_to_address_line1: "",
  ship_to_address_line2: "",
  ship_to_city: "",
  ship_to_state: "",
  ship_to_postal: "",
  ship_to_country: "",
  // Customer profile only (invoice delivery)
  invoice_send_to_email: "",
  invoice_cc_emails: "",
});

const ConfigureBillingProfile = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const initializedRef = useRef(false);
  const mspInitialFormRef = useRef(null);
  const customerInitialFormRef = useRef(null);

  const isMSPUser = isMSP();
  const {
    billingProfile,
    isFetchingProfile,
    fetchProfileError,
    customers,
    isFetchingCustomers,
    isUpdatingProfile,
    updateProfileSuccess,
    updateProfileError,
  } = useSelector((state) => state.billingManagement);

  const [customersList, setCustomersList] = useState([]);
  const [mspProfile, setMspProfile] = useState(defaultProfile());
  const [mspModalOpen, setMspModalOpen] = useState(false);
  const [customerEditModalOpen, setCustomerEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState(defaultProfile());
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploadPhase, setLogoUploadPhase] = useState(null);
  const [logoMessages, setLogoMessages] = useState([]);
  const [logoWsOpen, setLogoWsOpen] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [logoViewUrl, setLogoViewUrl] = useState(null);
  const [logoViewLoading, setLogoViewLoading] = useState(false);
  const [logoViewError, setLogoViewError] = useState(null);
  const [logoImageLoadFailed, setLogoImageLoadFailed] = useState(false);
  const [logoReplacedInSession, setLogoReplacedInSession] = useState(false);
  const [logoPreviewOpen, setLogoPreviewOpen] = useState(false);
  const [deleteLogoLoading, setDeleteLogoLoading] = useState(false);
  const [remitToAddressValidationStatus, setRemitToAddressValidationStatus] = useState(null);
  const [remitToAddressValidationMessage, setRemitToAddressValidationMessage] = useState("");
  const [soldToAddressValidationStatus, setSoldToAddressValidationStatus] = useState(null);
  const [soldToAddressValidationMessage, setSoldToAddressValidationMessage] = useState("");
  const [shipToAddressValidationStatus, setShipToAddressValidationStatus] = useState(null);
  const [shipToAddressValidationMessage, setShipToAddressValidationMessage] = useState("");
  const logoWsRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [touched, setTouched] = useState({});
  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  // Customer edit: email lists from metadata (kind=contacts_customer for send-to, kind=contacts for cc)
  const [invoiceSendToEmailOptions, setInvoiceSendToEmailOptions] = useState([]);
  const [ccEmailOptions, setCcEmailOptions] = useState([]);
  const [customerEmailOptionsLoading, setCustomerEmailOptionsLoading] = useState(false);

  const filteredCustomers = useMemo(() => {
    if (!Array.isArray(customersList)) return [];
    if (!searchTerm.trim()) return customersList;
    const searchLower = searchTerm.toLowerCase().trim();
    return customersList.filter((c) => {
      if (!c) return false;
      return (
        (c.org_name && c.org_name.toLowerCase().includes(searchLower)) ||
        (c.org_code && c.org_code.toLowerCase().includes(searchLower)) ||
        ((c.profile?.description || "").toLowerCase().includes(searchLower))
      );
    });
  }, [customersList, searchTerm]);

  const paginatedCustomers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredCustomers.slice(start, start + rowsPerPage);
  }, [filteredCustomers, page, rowsPerPage]);

  const isRecurring = editForm.billing_mode !== "ONDEMAND";
  const remitToFields = ["remit_to_name", "remit_to_address_line1", "remit_to_address_line2", "remit_to_city", "remit_to_state", "remit_to_postal", "remit_to_country", "remit_to_email", "remit_to_phone", "remit_to_fax", "remit_to_fein", "remit_to_duns", "remit_to_gst", "remit_to_contract_refs", "remit_to_ueid", "remit_to_pst", "logo_s3_uri"];
  const soldShipFields = ["sold_to_name", "sold_to_attn", "sold_to_address_line1", "sold_to_address_line2", "sold_to_city", "sold_to_state", "sold_to_postal", "sold_to_country", "ship_to_name", "ship_to_attn", "ship_to_address_line1", "ship_to_address_line2", "ship_to_city", "ship_to_state", "ship_to_postal", "ship_to_country"];
  const hasMspFormChanges = mspModalOpen && mspInitialFormRef.current && (
    (editForm.description ?? "") !== (mspInitialFormRef.current.description ?? "") ||
    Number(editForm.invoice_date) !== Number(mspInitialFormRef.current.invoice_date) ||
    remitToFields.some((f) => (editForm[f] ?? "") !== (mspInitialFormRef.current[f] ?? "")) ||
    logoReplacedInSession
  );
  const hasCustomerFormChanges = customerEditModalOpen && editingCustomer && customerInitialFormRef.current && (
    (editForm.description ?? "") !== (customerInitialFormRef.current.description ?? "") ||
    Number(editForm.invoice_date) !== Number(customerInitialFormRef.current.invoice_date) ||
    (editForm.billing_mode || "RECURRING") !== (customerInitialFormRef.current.billing_mode || "RECURRING") ||
    soldShipFields.some((f) => (editForm[f] ?? "") !== (customerInitialFormRef.current[f] ?? "")) ||
    (editForm.invoice_send_to_email ?? "").trim() !== (customerInitialFormRef.current.invoice_send_to_email ?? "").trim() ||
    (editForm.invoice_cc_emails ?? "").trim() !== (customerInitialFormRef.current.invoice_cc_emails ?? "").trim()
  );

  const remitRequiredFilled = ["remit_to_name", "remit_to_country", "remit_to_address_line1", "remit_to_city", "remit_to_state", "remit_to_postal"].every((f) => (editForm[f] ?? "").trim().length > 0);
  const remitEmailOrPhone = ((editForm.remit_to_email ?? "").trim().length > 0) || ((editForm.remit_to_phone ?? "").trim().length > 0);
  const isMspFormValid = hasMspFormChanges && remitRequiredFilled && remitEmailOrPhone;

  const soldRequiredFilled = ["sold_to_name", "sold_to_country", "sold_to_address_line1", "sold_to_city", "sold_to_state", "sold_to_postal"].every((f) => (editForm[f] ?? "").trim().length > 0);
  const shipRequiredFields = ["ship_to_name", "ship_to_address_line1", "ship_to_city", "ship_to_state", "ship_to_postal", "ship_to_country"];
  const shipComplete = shipRequiredFields.every((f) => (editForm[f] ?? "").trim().length > 0) || shipRequiredFields.every((f) => !(editForm[f] ?? "").trim().length);
  const invoiceSendToEmailFilled = (editForm.invoice_send_to_email ?? "").trim().length > 0;
  const isCustomerFormValid = hasCustomerFormChanges && soldRequiredFilled && shipComplete && invoiceSendToEmailFilled;

  useEffect(() => {
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }
    // Clear any stale success/error messages from previous visits or other billing pages
    // so we don't show e.g. "An unexpected error occurred" when just landing on this page
    dispatch(clearBillingManagementMessages());
    dispatch(getBillingProfile());
  }, [isMSPUser, history, dispatch]);

  useEffect(() => {
    if (isFetchingProfile) return;
    if (billingProfile && (billingProfile.msp_profile || billingProfile.customers)) {
      if (billingProfile.msp_profile) setMspProfile({ ...defaultProfile(), ...billingProfile.msp_profile });
      setCustomersList(Array.isArray(billingProfile.customers) ? billingProfile.customers : []);
      initializedRef.current = true;
      return;
    }
    if (billingProfile === null && !fetchProfileError) {
      dispatch(getCustomers({ page: 1, page_size: 100 }));
    }
  }, [isFetchingProfile, billingProfile, fetchProfileError, dispatch]);

  // Refetch profile after save so table and button state stay in sync
  useEffect(() => {
    if (updateProfileSuccess) {
      dispatch(getBillingProfile());
    }
  }, [updateProfileSuccess, dispatch]);

  useEffect(() => {
    if (billingProfile !== null || !customers?.length || initializedRef.current) return;
    initializedRef.current = true;
    setMspProfile(defaultProfile());
    setCustomersList(
      customers.map((c) => ({
        org_code: c.org_code,
        org_name: c.org_name,
        profile: { name: "Default", description: "", invoice_date: 15 },
      }))
    );
  }, [billingProfile, customers]);

  useEffect(() => {
    if (updateProfileSuccess) {
      setSnackbar({ open: true, message: "Billing profile saved successfully.", severity: "success" });
      dispatch(clearBillingManagementMessages());
      dispatch(getBillingProfile());
      setMspModalOpen(false);
      setCustomerEditModalOpen(false);
      setEditingCustomer(null);
    }
  }, [updateProfileSuccess, dispatch]);

  useEffect(() => {
    if (updateProfileError) {
      const msg = typeof updateProfileError === "string" ? updateProfileError : (getApiErrorMessage(updateProfileError) || "Failed to save billing profile.");
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  }, [updateProfileError]);

  const hasMspProfile = billingProfile?.msp_profile && (billingProfile.msp_profile.name != null || billingProfile.msp_profile.invoice_date != null);
  const customerActionsDisabled = billingProfile == null || billingProfile?.msp_profile == null;

  const handleOpenMspModal = () => {
    const logoVal = (mspProfile.logo_s3_uri ?? "").trim();
    const logoFileName = logoVal.startsWith("s3://") ? (logoVal.split("/").pop() || logoVal) : logoVal;
    const initial = {
      ...defaultProfile(),
      ...mspProfile,
      name: mspProfile.org_name ?? mspProfile.name ?? "",
      logo_s3_uri: logoFileName,
    };
    mspInitialFormRef.current = { ...initial };
    setEditForm(initial);
    setLogoFile(null);
    setLogoUploadPhase(null);
    setLogoMessages([]);
    setLogoError(null);
    setLogoViewUrl(null);
    setLogoViewError(null);
    setLogoViewLoading(true);
    setLogoImageLoadFailed(false);
    setLogoReplacedInSession(false);
    setLogoPreviewOpen(false);
    setTouched({});
    setMspModalOpen(true);
  };

  const handleSaveMspProfile = () => {
    const invoiceDate = parseInt(editForm.invoice_date, 10);
    if (isNaN(invoiceDate) || invoiceDate < 1 || invoiceDate > 31) {
      setSnackbar({ open: true, message: "Invoice date must be between 1 and 31.", severity: "error" });
      return;
    }
    const remitRequired = ["remit_to_name", "remit_to_country", "remit_to_address_line1", "remit_to_city", "remit_to_state", "remit_to_postal"];
    const missingRemit = remitRequired.filter((f) => !(editForm[f] ?? "").trim());
    if (missingRemit.length > 0) {
      setSnackbar({ open: true, message: "Please fill all required Remit-To fields (Name, Country, Address, City, State, Postal).", severity: "error" });
      return;
    }
    const hasRemitEmail = (editForm.remit_to_email ?? "").trim().length > 0;
    const hasRemitPhone = (editForm.remit_to_phone ?? "").trim().length > 0;
    if (!hasRemitEmail && !hasRemitPhone) {
      setSnackbar({ open: true, message: "At least one of Remit-To Email or Phone is required.", severity: "error" });
      return;
    }
    const payload = {
      name: editForm.name?.trim() ?? "",
      description: editForm.description?.trim() ?? "",
      invoice_date: invoiceDate,
      billing_mode: "RECURRING",
      remit_to_name: editForm.remit_to_name?.trim() ?? "",
      remit_to_address_line1: editForm.remit_to_address_line1?.trim() ?? "",
      remit_to_address_line2: editForm.remit_to_address_line2?.trim() ?? "",
      remit_to_city: editForm.remit_to_city?.trim() ?? "",
      remit_to_state: editForm.remit_to_state?.trim() ?? "",
      remit_to_postal: editForm.remit_to_postal?.trim() ?? "",
      remit_to_country: editForm.remit_to_country?.trim() ?? "",
      remit_to_email: editForm.remit_to_email?.trim() ?? "",
      remit_to_phone: editForm.remit_to_phone?.trim() ?? "",
      remit_to_fax: editForm.remit_to_fax?.trim() ?? "",
      remit_to_fein: editForm.remit_to_fein?.trim() ?? "",
      remit_to_duns: editForm.remit_to_duns?.trim() ?? "",
      remit_to_gst: editForm.remit_to_gst?.trim() ?? "",
      remit_to_contract_refs: editForm.remit_to_contract_refs?.trim() ?? "",
      remit_to_ueid: editForm.remit_to_ueid?.trim() ?? "",
      remit_to_pst: editForm.remit_to_pst?.trim() ?? "",
    };
    const logoVal = (editForm.logo_s3_uri ?? "").trim();
    if (logoVal) {
      payload.logo_s3_uri = logoVal.startsWith("s3://") ? logoVal.split("/").pop() || logoVal : logoVal;
    }
    dispatch(updateBillingProfile(payload));
  };

  // Normalize metadata email list to string[] (API returns { kind, values: string[] } or { items } or array).
  const normalizeEmailList = (data) => {
    if (!data) return [];
    const raw = Array.isArray(data.values)
      ? data.values
      : Array.isArray(data.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];
    return raw.map((x) => (typeof x === "string" ? x : x?.email ?? x?.value ?? "")).filter(Boolean);
  };

  const handleOpenCustomerEdit = (customer) => {
    setEditingCustomer(customer);
    const profile = customer?.profile || {};
    const billingMode = profile.billing_mode === "ONDEMAND" || profile.billing_mode === "ON_DEMAND" ? "ONDEMAND" : (profile.billing_mode || "RECURRING");
    const initial = {
      ...defaultProfile(),
      ...profile,
      billing_mode: billingMode,
      name: customer.org_name ?? profile.name ?? "",
      invoice_send_to_email: profile.invoice_send_to_email ?? "",
      invoice_cc_emails: profile.invoice_cc_emails ?? "",
    };
    customerInitialFormRef.current = { ...initial };
    setEditForm(initial);
    setTouched({});
    setSoldToAddressValidationStatus(null);
    setSoldToAddressValidationMessage("");
    setShipToAddressValidationStatus(null);
    setShipToAddressValidationMessage("");
    setInvoiceSendToEmailOptions([]);
    setCcEmailOptions([]);
    setCustomerEditModalOpen(true);
    const orgCode = customer?.org_code;
    if (orgCode) {
      setCustomerEmailOptionsLoading(true);
      Promise.all([
        getCustomerMetadata(orgCode, { kind: "contacts_customer" }),
        getCustomerMetadata(orgCode, { kind: "contacts" }),
      ])
        .then(([sendToRes, ccRes]) => {
          const sendToData = sendToRes?.data ?? sendToRes;
          const ccData = ccRes?.data ?? ccRes;
          setInvoiceSendToEmailOptions(normalizeEmailList(sendToData));
          setCcEmailOptions(normalizeEmailList(ccData));
        })
        .catch(() => {
          setInvoiceSendToEmailOptions([]);
          setCcEmailOptions([]);
        })
        .finally(() => setCustomerEmailOptionsLoading(false));
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logoMessages]);

  useEffect(() => {
    return () => {
      if (logoWsRef.current) logoWsRef.current.close();
    };
  }, []);

  // Fetch current logo view URL when edit default billing profile modal opens
  useEffect(() => {
    if (!mspModalOpen) return;
    let cancelled = false;
    get(GET_BILLING_PROFILE_LOGO_VIEW)
      .then((res) => {
        if (cancelled) return;
        setLogoViewLoading(false);
        const status = res?.status;
        if (status === 200 && res?.view_url) {
          setLogoViewUrl(res.view_url);
          setLogoViewError(null);
          setLogoImageLoadFailed(false);
        } else if (status === 404) {
          setLogoViewUrl(null);
          setLogoViewError(null);
        } else {
          setLogoViewUrl(null);
          setLogoViewError(getApiErrorMessage(res?.data || res) || "Could not load logo.");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLogoViewLoading(false);
          setLogoViewError("Could not load logo.");
        }
      });
    return () => { cancelled = true; };
  }, [mspModalOpen]);

  const handleDeleteLogo = async () => {
    setDeleteLogoLoading(true);
    try {
      const res = await deleteBillingProfileLogo();
      const status = res?.status;
      if (status === 204 || status === 200) {
        setLogoViewUrl(null);
        setEditForm((p) => ({ ...p, logo_s3_uri: "" }));
        setMspProfile((prev) => ({ ...prev, logo_s3_uri: "" }));
        dispatch(getBillingProfile());
        setSnackbar({ open: true, message: "Logo deleted successfully.", severity: "success" });
        setLogoPreviewOpen(false);
      } else {
        const msg = getApiErrorMessage(res?.data ?? res) || "Failed to delete logo.";
        setSnackbar({ open: true, message: msg, severity: "error" });
      }
    } catch (err) {
      const msg = err?.response?.data != null ? getApiErrorMessage(err.response.data) : (err?.message || "Failed to delete logo.");
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setDeleteLogoLoading(false);
    }
  };

  const validateRemitToAddress = async () => {
    const line1 = (editForm.remit_to_address_line1 ?? "").trim();
    const country = (editForm.remit_to_country ?? "").trim();
    if (!line1 || !country) return;

    setRemitToAddressValidationStatus(null);
    setRemitToAddressValidationMessage("Validating address...");

    const regionCode = getRegionCode(country);
    if (!regionCode) {
      setRemitToAddressValidationStatus("invalid");
      setRemitToAddressValidationMessage("Please select a valid country first");
      return;
    }

    const line2 = (editForm.remit_to_address_line2 ?? "").trim();
    const city = (editForm.remit_to_city ?? "").trim();
    const state = (editForm.remit_to_state ?? "").trim();
    const postal = (editForm.remit_to_postal ?? "").trim();
    const addressLines = [line1];
    if (line2) addressLines.push(line2);
    if (city) addressLines.push(city);
    if (state || postal) addressLines.push([state, postal].filter(Boolean).join(" "));
    const addressString = addressLines.join(", ");

    try {
      const result = await validateAddressWithAPI(
        addressString,
        regionCode,
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      );
      setRemitToAddressValidationStatus(result.isValid ? "valid" : "invalid");
      setRemitToAddressValidationMessage(result.message);
    } catch {
      setRemitToAddressValidationStatus("invalid");
      setRemitToAddressValidationMessage("Failed to validate address. Please try again.");
    }
  };

  const validateSoldToAddress = async () => {
    const line1 = (editForm.sold_to_address_line1 ?? "").trim();
    const country = (editForm.sold_to_country ?? "").trim();
    if (!line1 || !country) return;

    setSoldToAddressValidationStatus(null);
    setSoldToAddressValidationMessage("Validating address...");

    const regionCode = getRegionCode(country);
    if (!regionCode) {
      setSoldToAddressValidationStatus("invalid");
      setSoldToAddressValidationMessage("Please select a valid country first");
      return;
    }

    const line2 = (editForm.sold_to_address_line2 ?? "").trim();
    const city = (editForm.sold_to_city ?? "").trim();
    const state = (editForm.sold_to_state ?? "").trim();
    const postal = (editForm.sold_to_postal ?? "").trim();
    const addressLines = [line1];
    if (line2) addressLines.push(line2);
    if (city) addressLines.push(city);
    if (state || postal) addressLines.push([state, postal].filter(Boolean).join(" "));
    const addressString = addressLines.join(", ");

    try {
      const result = await validateAddressWithAPI(
        addressString,
        regionCode,
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      );
      setSoldToAddressValidationStatus(result.isValid ? "valid" : "invalid");
      setSoldToAddressValidationMessage(result.message);
    } catch {
      setSoldToAddressValidationStatus("invalid");
      setSoldToAddressValidationMessage("Failed to validate address. Please try again.");
    }
  };

  const validateShipToAddress = async () => {
    const line1 = (editForm.ship_to_address_line1 ?? "").trim();
    const country = (editForm.ship_to_country ?? "").trim();
    if (!line1 || !country) return;

    setShipToAddressValidationStatus(null);
    setShipToAddressValidationMessage("Validating address...");

    const regionCode = getRegionCode(country);
    if (!regionCode) {
      setShipToAddressValidationStatus("invalid");
      setShipToAddressValidationMessage("Please select a valid country first");
      return;
    }

    const line2 = (editForm.ship_to_address_line2 ?? "").trim();
    const city = (editForm.ship_to_city ?? "").trim();
    const state = (editForm.ship_to_state ?? "").trim();
    const postal = (editForm.ship_to_postal ?? "").trim();
    const addressLines = [line1];
    if (line2) addressLines.push(line2);
    if (city) addressLines.push(city);
    if (state || postal) addressLines.push([state, postal].filter(Boolean).join(" "));
    const addressString = addressLines.join(", ");

    try {
      const result = await validateAddressWithAPI(
        addressString,
        regionCode,
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      );
      setShipToAddressValidationStatus(result.isValid ? "valid" : "invalid");
      setShipToAddressValidationMessage(result.message);
    } catch {
      setShipToAddressValidationStatus("invalid");
      setShipToAddressValidationMessage("Failed to validate address. Please try again.");
    }
  };

  const handleLogoFileSelect = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const name = file.name || "";
    if (!ALLOWED_LOGO_EXT.test(name)) {
      setSnackbar({ open: true, message: "Logo must be PNG or JPEG.", severity: "error" });
      return;
    }
    if (file.size === 0) {
      setLogoError("File is empty");
      return;
    }
    if (!isValidLogoFileName(name)) {
      setSnackbar({
        open: true,
        message: "Invalid file name. Use only letters, numbers, dots, hyphens, and underscores. No spaces or special characters; keep the name under 255 characters.",
        severity: "error",
      });
      return;
    }
    setLogoFile(file);
    setLogoError(null);
    setLogoMessages([]);
    setLogoUploadPhase("url");
    try {
      const res = await getBillingProfileLogoUploadUrl({ file_name: name });
      const data = res?.data ?? res;
      const uploadUrl = data.upload_url || data.uploadUrl;
      const uniqueFileName = data.file_name || data.fileName;
      const s3Key = data.s3_key || data.s3Key;
      const apiContentType = data.content_type || data.contentType;
      const ext = name.split(".").pop()?.toLowerCase();
      const contentType =
        apiContentType ||
        file.type ||
        LOGO_MIME_BY_EXT[ext] ||
        "application/octet-stream";
      if (!uploadUrl) {
        const apiMsg = getApiErrorMessage(data);
        setLogoError(apiMsg !== "Request failed." ? apiMsg : "Could not get upload URL");
        setLogoUploadPhase(null);
        return;
      }
      setLogoUploadPhase("upload");
      // If API didn't return content_type, backend likely signed the URL without it — omit header to avoid 403.
      const putOptions = {
        method: "PUT",
        body: apiContentType ? file : await file.arrayBuffer(),
        ...(apiContentType ? { headers: { "Content-Type": apiContentType } } : {}),
      };
      const putRes = await fetch(uploadUrl, putOptions);
      if (!putRes.ok) {
        const message = putRes.status === 403
          ? "Upload denied (403). File type may not match; try again or contact support."
          : "Upload failed";
        setLogoError(message);
        setLogoUploadPhase(null);
        return;
      }
      setLogoUploadPhase("scan");
      if (BILLING_LOGO_WEBSOCKET_URL && uniqueFileName) {
        const token = JSON.parse(localStorage.getItem("authUser") || "{}").accessToken;
        const ws = new WebSocket(`${BILLING_LOGO_WEBSOCKET_URL}?token=${token}`);
        logoWsRef.current = ws;
        ws.onopen = () => {
          setLogoWsOpen(true);
          ws.send(JSON.stringify({ action: "sendMessage", fileName: uniqueFileName }));
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            setLogoMessages((prev) => [...prev, { text: msg.Message, status: msg.Status, timestamp: new Date().toLocaleTimeString() }]);
            // Enable Save only after WebSocket confirms scan success; use API-returned fileName for payload
            if (msg.Message && msg.Message.includes("Malware scan completed successfully") && String(msg.Status).toLowerCase() === "success") {
              setEditForm((p) => ({ ...p, logo_s3_uri: uniqueFileName }));
              setLogoReplacedInSession(true);
              ws.close();
            }
          } catch (_) {}
        };
        ws.onclose = () => {
          setLogoWsOpen(false);
          setLogoUploadPhase(null);
          logoWsRef.current = null;
        };
        ws.onerror = () => {
          setLogoError("WebSocket error");
          setLogoUploadPhase(null);
        };
      } else {
        // No WebSocket: upload done but no scan confirmation — do not enable Save on logo alone; use API-returned fileName
        setEditForm((p) => ({ ...p, logo_s3_uri: uniqueFileName }));
        setLogoUploadPhase(null);
      }
    } catch (err) {
      const apiMsg = err?.response?.data != null ? getApiErrorMessage(err.response.data) : null;
      setLogoError(apiMsg || err?.message || "Logo upload failed");
      setLogoUploadPhase(null);
    }
    e.target.value = "";
  };

  const fillSoldToFromRemitTo = () => {
    if (!mspProfile) return;
    setEditForm((p) => ({
      ...p,
      sold_to_name: mspProfile.remit_to_name ?? "",
      sold_to_address_line1: mspProfile.remit_to_address_line1 ?? "",
      sold_to_address_line2: mspProfile.remit_to_address_line2 ?? "",
      sold_to_city: mspProfile.remit_to_city ?? "",
      sold_to_state: mspProfile.remit_to_state ?? "",
      sold_to_postal: mspProfile.remit_to_postal ?? "",
      sold_to_country: mspProfile.remit_to_country ?? "",
    }));
  };

  const fillShipToFromSoldTo = () => {
    setEditForm((p) => ({
      ...p,
      ship_to_name: p.sold_to_name ?? "",
      ship_to_attn: p.sold_to_attn ?? "",
      ship_to_address_line1: p.sold_to_address_line1 ?? "",
      ship_to_address_line2: p.sold_to_address_line2 ?? "",
      ship_to_city: p.sold_to_city ?? "",
      ship_to_state: p.sold_to_state ?? "",
      ship_to_postal: p.sold_to_postal ?? "",
      ship_to_country: p.sold_to_country ?? "",
    }));
  };

  const handleSaveCustomerProfile = () => {
    if (!editingCustomer) return;
    const isOnDemand = editForm.billing_mode === "ONDEMAND";
    if (!isOnDemand) {
      const invoiceDate = parseInt(editForm.invoice_date, 10);
      if (isNaN(invoiceDate) || invoiceDate < 1 || invoiceDate > 31) {
        setSnackbar({ open: true, message: "Invoice date must be between 1 and 31.", severity: "error" });
        return;
      }
    }
    const sendToEmail = (editForm.invoice_send_to_email ?? "").trim();
    if (!sendToEmail) {
      setSnackbar({ open: true, message: "At least one primary recipient is required for invoice delivery.", severity: "error" });
      return;
    }
    const soldRequired = ["sold_to_name", "sold_to_country", "sold_to_address_line1", "sold_to_city", "sold_to_state", "sold_to_postal"];
    const missingSold = soldRequired.filter((f) => !(editForm[f] ?? "").trim());
    if (missingSold.length > 0) {
      setSnackbar({ open: true, message: "Please fill all required Sold-To fields (Name, Country, Address, City, State, Postal).", severity: "error" });
      return;
    }
    const shipRequiredFieldsList = ["ship_to_name", "ship_to_address_line1", "ship_to_city", "ship_to_state", "ship_to_postal", "ship_to_country"];
    const hasAnyShip = shipRequiredFieldsList.some((f) => (editForm[f] ?? "").trim().length > 0);
    const hasAllShip = shipRequiredFieldsList.every((f) => (editForm[f] ?? "").trim().length > 0);
    if (hasAnyShip && !hasAllShip) {
      setSnackbar({ open: true, message: "Ship-To is optional but when provided, Name, Address line 1, City, State, Postal and Country are required.", severity: "error" });
      return;
    }
    const payload = {
      name: editForm.name?.trim() ?? "",
      description: editForm.description?.trim() ?? "",
      billing_mode: isOnDemand ? "ON_DEMAND" : "RECURRING",
      customer_orgcode: editingCustomer.org_code,
      invoice_send_to_email: sendToEmail,
      sold_to_name: editForm.sold_to_name?.trim() ?? "",
      sold_to_attn: editForm.sold_to_attn?.trim() ?? "",
      sold_to_address_line1: editForm.sold_to_address_line1?.trim() ?? "",
      sold_to_address_line2: editForm.sold_to_address_line2?.trim() ?? "",
      sold_to_city: editForm.sold_to_city?.trim() ?? "",
      sold_to_state: editForm.sold_to_state?.trim() ?? "",
      sold_to_postal: editForm.sold_to_postal?.trim() ?? "",
      sold_to_country: editForm.sold_to_country?.trim() ?? "",
    };
    const ccEmails = (editForm.invoice_cc_emails ?? "").trim();
    if (ccEmails) payload.invoice_cc_emails = ccEmails;
    if (!isOnDemand) payload.invoice_date = Number(editForm.invoice_date);
    if (hasAllShip) {
      payload.ship_to_name = editForm.ship_to_name?.trim() ?? "";
      payload.ship_to_attn = editForm.ship_to_attn?.trim() ?? "";
      payload.ship_to_address_line1 = editForm.ship_to_address_line1?.trim() ?? "";
      payload.ship_to_address_line2 = editForm.ship_to_address_line2?.trim() ?? "";
      payload.ship_to_city = editForm.ship_to_city?.trim() ?? "";
      payload.ship_to_state = editForm.ship_to_state?.trim() ?? "";
      payload.ship_to_postal = editForm.ship_to_postal?.trim() ?? "";
      payload.ship_to_country = editForm.ship_to_country?.trim() ?? "";
    }
    dispatch(updateBillingProfile(payload));
  };

  const loading = isFetchingProfile || (billingProfile === null && isFetchingCustomers);

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      {/* Row 1: Breadcrumbs left, Back right */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Breadcrumbs
          sx={{
            fontSize: "0.875rem",
            "& .MuiBreadcrumbs-separator": { color: "#94a3b8", mx: 0.5 },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push("/billing-management")}
            sx={{ color: "#64748b", textDecoration: "none", cursor: "pointer", fontSize: "0.875rem", "&:hover": { color: "#0f172a" } }}
          >
            Billing Management
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#3b82f6",
              fontSize: "0.875rem",
              fontWeight: 600,
              px: 0.5,
            }}
          >
            Configure Billing Profiles
          </Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push("/billing-management")}
          sx={{ textTransform: "none", color: "#64748b", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}
        >
          Back
        </Button>
      </Box>

      {/* Row 2: Header left, Default billing profile button right */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "1.25rem", mb: 0.5 }}>
            Configure Billing Profiles
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem" }}>
            Customize invoice generation cycles and define unique billing and shipping entities for each client.
          </Typography>
        </Box>
        {!loading && !fetchProfileError && (
          hasMspProfile ? (
            <Button
              variant="contained"
              onClick={handleOpenMspModal}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8125rem",
                borderRadius: "6px",
                backgroundColor: "#3b82f6",
                "&:hover": { backgroundColor: "#2563eb" },
              }}
            >
              Default Billing Profile
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleOpenMspModal}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8125rem",
                borderRadius: "6px",
                backgroundColor: "#3b82f6",
                "&:hover": { backgroundColor: "#2563eb" },
              }}
            >
              Configure MSP Billing Profile
            </Button>
          )
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
        </Box>
      ) : fetchProfileError && billingProfile === null ? (
        <Card variant="outlined" sx={{ p: 3 }}>
          <Typography color="error">{typeof fetchProfileError === "string" ? fetchProfileError : (getApiErrorMessage(fetchProfileError) || "Failed to load billing profile.")}</Typography>
        </Card>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "rgba(0,0,0,0.06)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <TextField
                size="small"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#71717a" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: 380,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    fontFamily: "inherit",
                    backgroundColor: "#fafafa",
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: "6px",
                    height: "36px",
                    "&:hover": { borderColor: "rgba(0,0,0,0.12)", backgroundColor: "#fff" },
                    "&.Mui-focused": { borderColor: "#18181b", backgroundColor: "#fff", boxShadow: "0 0 0 2px rgba(24,24,27,0.1)" },
                    "& fieldset": { border: "none" },
                  },
                }}
              />
            </Box>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow sx={{ "& th": { backgroundColor: "#f4f4f5", borderBottom: "2px solid #e4e4e7", py: 1.5, px: 2, fontWeight: 600, fontSize: "0.875rem", color: "#52525b" } }}>
                    <TableCell>Customer</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">
                      Invoice Day
                      <Tooltip title="Date of every month the recurring invoice will be generated" placement="top">
                        <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: "1.125rem", color: "text.secondary", verticalAlign: "middle", cursor: "help" }} aria-label="Info" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">Purchase Order(s)</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#71717a", fontSize: "0.875rem" }}>
                        No customers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedCustomers.map((c) => (
                      <TableRow key={c.org_code} sx={{ "& td": { borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5, px: 2, fontSize: "0.875rem" } }}>
                        <TableCell sx={{ fontWeight: 500, color: "#18181b" }}>{c.org_name || c.org_code}</TableCell>
                        <TableCell sx={{ color: "#52525b" }}>{(c.profile?.description || "").trim() || "—"}</TableCell>
                        <TableCell align="center" sx={{ color: "#52525b" }}>
                          {(() => {
                            const formatted = formatOrdinalDay(c.profile?.invoice_date);
                            if (!formatted) return "—";
                            const num = formatted.replace(/\D/g, "");
                            const suffix = formatted.slice(num.length);
                            return <>{num}<Box component="sup" sx={{ fontSize: "0.65em", lineHeight: 0, verticalAlign: "super", ml: 0.25 }}>{suffix}</Box></>;
                          })()}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#52525b" }}>
                          {c.active_purchase_orders_count != null ? (
                            <Chip
                              label={c.active_purchase_orders_count}
                              size="small"
                              onClick={() => history.push(`/billing-management/customer/${c.org_code}/purchase-orders`, { customerName: c.org_name || c.org_code })}
                              sx={{
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                                "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.12)" },
                              }}
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={customerActionsDisabled ? "Configure MSP Billing Profile first" : "Edit customer Billing Profile"}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={customerActionsDisabled}
                                onClick={() => !customerActionsDisabled && handleOpenCustomerEdit(c)}
                                sx={{
                                  color: customerActionsDisabled ? "#d1d5db" : "#71717a",
                                  "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" },
                                }}
                                aria-label="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredCustomers.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                  px: 2,
                  py: 1.25,
                  minHeight: 48,
                  borderTop: "1px solid rgba(0,0,0,0.06)",
                  backgroundColor: "#fafafa",
                  "& .MuiSelect-select, & .MuiInputBase-input": { py: 0.75 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography component="span" sx={{ fontSize: "0.8125rem", color: "#52525b", fontWeight: 500, lineHeight: 1 }}>
                    Rows per page
                  </Typography>
                  <FormControl size="small" variant="outlined" sx={{ minWidth: 72 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(0);
                      }}
                      sx={{
                        fontSize: "0.8125rem",
                        height: 32,
                        backgroundColor: "#fff",
                        "& .MuiSelect-select": { py: "6px", lineHeight: 1.5 },
                        "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
                      }}
                    >
                      {[10, 15, 25, 50].map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.8125rem", color: "#52525b", fontWeight: 500, lineHeight: 1 }}>
                    {filteredCustomers.length === 0
                      ? "0–0 of 0"
                      : `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredCustomers.length)} of ${filteredCustomers.length}`}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      disabled={page <= 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      aria-label="Previous page"
                      sx={{
                        color: page <= 0 ? "#d4d4d8" : "#52525b",
                        "&:hover:not(.Mui-disabled)": { backgroundColor: "rgba(0,0,0,0.04)", color: "#18181b" },
                      }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={page >= Math.ceil(filteredCustomers.length / rowsPerPage) - 1}
                      onClick={() => setPage((p) => Math.min(Math.ceil(filteredCustomers.length / rowsPerPage) - 1, p + 1))}
                      aria-label="Next page"
                      sx={{
                        color: page >= Math.ceil(filteredCustomers.length / rowsPerPage) - 1 ? "#d4d4d8" : "#52525b",
                        "&:hover:not(.Mui-disabled)": { backgroundColor: "rgba(0,0,0,0.04)", color: "#18181b" },
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* MSP / Default profile modal */}
      <Dialog open={mspModalOpen} onClose={() => { setMspModalOpen(false); setLogoViewUrl(null); setLogoViewError(null); setLogoPreviewOpen(false); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.125rem" }}>{hasMspProfile ? "Edit" : "Configure"} Billing Profile{mspProfile?.org_name ? ` — ${mspProfile.org_name}` : ""}</DialogTitle>
        <DialogContent sx={{ pt: 1, overflow: "auto" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField label="Description" value={editForm.description || ""} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} size="small" fullWidth multiline rows={2} />
            <FormControl size="small" fullWidth required>
              <InputLabel>Invoice date (day of month)</InputLabel>
              <Select value={editForm.invoice_date ?? 15} label="Invoice date (day of month)" onChange={(e) => setEditForm((p) => ({ ...p, invoice_date: Number(e.target.value) }))}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day}>{formatOrdinalDay(day)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151", mt: 1 }}>Remit-To</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pl: 0 }}>
              <TextField label="Name" required value={editForm.remit_to_name || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_name: e.target.value }))} onBlur={() => markTouched("remit_to_name")} size="small" fullWidth error={touched.remit_to_name && !editForm.remit_to_name?.trim()} />
              <Autocomplete
                size="small"
                fullWidth
                options={countryList}
                getOptionLabel={(option) => option?.name ?? ""}
                value={countryList.find((c) => c.name === (editForm.remit_to_country ?? "")) ?? null}
                onChange={(e, newValue) => {
                  setEditForm((p) => ({ ...p, remit_to_country: newValue?.name ?? "" }));
                  setRemitToAddressValidationStatus(null);
                  setRemitToAddressValidationMessage("");
                }}
                onBlur={() => markTouched("remit_to_country")}
                isOptionEqualToValue={(option, value) => option?.name === value?.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    required
                    error={touched.remit_to_country && !editForm.remit_to_country?.trim()}
                  />
                )}
              />
              <TextField label="Address line 1" required value={editForm.remit_to_address_line1 || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_address_line1: e.target.value }))} onBlur={() => { markTouched("remit_to_address_line1"); validateRemitToAddress(); }} size="small" fullWidth error={(touched.remit_to_address_line1 && !editForm.remit_to_address_line1?.trim()) || remitToAddressValidationStatus === "invalid"} InputProps={{ endAdornment: remitToAddressValidationStatus === "valid" ? <InputAdornment position="end"><CheckCircleIcon color="success" fontSize="small" /></InputAdornment> : null }} />
              {remitToAddressValidationMessage && <Typography variant="caption" sx={{ color: remitToAddressValidationStatus === "valid" ? "success.main" : remitToAddressValidationStatus === "invalid" ? "error.main" : "text.secondary" }}>{remitToAddressValidationMessage}</Typography>}
              <TextField label="Address line 2" value={editForm.remit_to_address_line2 || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_address_line2: e.target.value }))} size="small" fullWidth />
              <TextField label="City" required value={editForm.remit_to_city || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_city: e.target.value }))} onBlur={() => markTouched("remit_to_city")} size="small" fullWidth error={touched.remit_to_city && !editForm.remit_to_city?.trim()} />
              <TextField label="State" required value={editForm.remit_to_state || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_state: e.target.value }))} onBlur={() => markTouched("remit_to_state")} size="small" fullWidth error={touched.remit_to_state && !editForm.remit_to_state?.trim()} />
              <TextField label="Postal code" required value={editForm.remit_to_postal || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_postal: e.target.value }))} onBlur={() => markTouched("remit_to_postal")} size="small" fullWidth error={touched.remit_to_postal && !editForm.remit_to_postal?.trim()} />
              <TextField label="Email" type="email" value={editForm.remit_to_email || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_email: e.target.value }))} onBlur={() => markTouched("remit_to_email")} size="small" fullWidth helperText="At least one of Email or Phone is required" error={(touched.remit_to_email || touched.remit_to_phone) && !(editForm.remit_to_email?.trim() || editForm.remit_to_phone?.trim())} />
              <TextField label="Phone" value={editForm.remit_to_phone || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_phone: e.target.value }))} onBlur={() => markTouched("remit_to_phone")} size="small" fullWidth error={(touched.remit_to_email || touched.remit_to_phone) && !(editForm.remit_to_email?.trim() || editForm.remit_to_phone?.trim())} />
              <TextField label="Fax" value={editForm.remit_to_fax || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_fax: e.target.value }))} size="small" fullWidth />
              <TextField label="FEIN" value={editForm.remit_to_fein || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_fein: e.target.value }))} size="small" fullWidth />
              <TextField label="DUNS" value={editForm.remit_to_duns || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_duns: e.target.value }))} size="small" fullWidth />
              <TextField label="GST" value={editForm.remit_to_gst || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_gst: e.target.value }))} size="small" fullWidth />
              <TextField label="Contract refs" value={editForm.remit_to_contract_refs || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_contract_refs: e.target.value }))} size="small" fullWidth multiline rows={2} placeholder="Optional reference(s)" />
              <TextField label="UEID" value={editForm.remit_to_ueid || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_ueid: e.target.value }))} size="small" fullWidth />
              <TextField label="PST" value={editForm.remit_to_pst || ""} onChange={(e) => setEditForm((p) => ({ ...p, remit_to_pst: e.target.value }))} size="small" fullWidth />
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151", mt: 1 }}>Logo (optional)</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem", mt: -0.5, display: "block" }}>
              Accepted: PNG or JPEG. File name must contain only letters, numbers, dots, hyphens, and underscores, no spaces, no special characters and be under 255 characters.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {logoViewLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="caption" color="text.secondary">Loading current logo…</Typography>
                </Box>
              )}
              {!logoViewLoading && logoViewUrl && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {!logoImageLoadFailed ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Box
                        component="img"
                        src={logoViewUrl}
                        alt="Current billing profile logo"
                        onError={() => setLogoImageLoadFailed(true)}
                        onClick={() => setLogoPreviewOpen(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLogoPreviewOpen(true); } }}
                        sx={{
                          maxHeight: 80, maxWidth: 200, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 0.5,
                          cursor: "pointer", "&:hover": { borderColor: "primary.main", boxShadow: 1 },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">Current logo (click to view larger)</Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      Current logo could not be displayed. You can replace it below.
                    </Typography>
                  )}
                </Box>
              )}
              {logoViewError && (
                <Typography variant="caption" color="error">{logoViewError}</Typography>
              )}
              <input
                ref={logoFileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleLogoFileSelect}
                style={{ display: "none" }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  disabled={!!logoUploadPhase}
                  onClick={() => logoFileInputRef.current?.click()}
                  sx={{ height: 36, minHeight: 36, textTransform: "none" }}
                >
                  {editForm.logo_s3_uri ? "Replace logo" : "Upload logo"}
                </Button>
                {logoViewUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={handleDeleteLogo}
                    disabled={deleteLogoLoading || !!logoUploadPhase}
                    sx={{ textTransform: "none", height: 36, minHeight: 36 }}
                  >
                    {deleteLogoLoading ? "Deleting…" : "Delete logo"}
                  </Button>
                )}
              </Box>
              {logoUploadPhase && (logoUploadPhase !== "scan" || logoMessages.length === 0) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">{logoUploadPhase === "url" ? "Getting upload URL…" : logoUploadPhase === "upload" ? "Uploading…" : "Scanning…"}</Typography>
                </Box>
              )}
              {logoError && <Typography variant="caption" color="error">{logoError}</Typography>}
              {logoMessages.length > 0 && (
                <Paper elevation={0} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, maxHeight: 120, overflow: "auto" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {logoMessages.map((msg, i) => (
                      <Fade in key={i}>
                        <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: msg.status?.toLowerCase() === "success" ? "success.light" : "info.light", fontSize: "0.75rem" }}>{msg.text}</Box>
                      </Fade>
                    ))}
                    {logoWsOpen && <CircularProgress size={16} />}
                  </Box>
                  <div ref={messagesEndRef} />
                </Paper>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={() => setMspModalOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveMspProfile} disabled={isUpdatingProfile || !isMspFormValid} sx={{ textTransform: "none" }}>
            {isUpdatingProfile ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logo preview — view current logo larger */}
      <Dialog open={logoPreviewOpen} onClose={() => setLogoPreviewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxWidth: 560 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>Current logo</DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2, bgcolor: "grey.50" }}>
          {logoViewUrl && (
            <Box component="img" src={logoViewUrl} alt="Billing profile logo (full size)" sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setLogoPreviewOpen(false)} sx={{ textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Customer profile edit modal — sold-to required; ship-to optional; no remit-to, no logo */}
      <Dialog open={customerEditModalOpen} onClose={() => { setCustomerEditModalOpen(false); setEditingCustomer(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
          Edit Billing Profile {editingCustomer?.org_name ? `— ${editingCustomer.org_name}` : ""}
        </DialogTitle>
        <DialogContent sx={{ pt: 1, overflow: "auto" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField label="Description" value={editForm.description || ""} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} size="small" fullWidth multiline rows={2} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151", mb: -0.5 }}>
              Billing Mode
            </Typography>
            <ToggleButtonGroup
              value={editForm.billing_mode === "ONDEMAND" ? "ONDEMAND" : "RECURRING"}
              exclusive
              onChange={(_, value) => {
                if (value == null) return;
                setEditForm((p) => ({
                  ...p,
                  billing_mode: value,
                  ...(value === "RECURRING" ? { invoice_date: "" } : {}),
                }));
              }}
              fullWidth
              sx={{
                "& .MuiToggleButtonGroup-grouped": { flex: 1 },
                "& .MuiToggleButton-root": {
                  py: 1.5,
                  textTransform: "none",
                  border: "1px solid #e5e7eb",
                  "&.Mui-selected": {
                    bgcolor: "rgba(59, 130, 246, 0.08)",
                    borderColor: "#3b82f6",
                    color: "#2563eb",
                    "&:hover": { bgcolor: "rgba(59, 130, 246, 0.12)" },
                  },
                },
              }}
            >
              <ToggleButton value="RECURRING" aria-label="Recurring">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ScheduleIcon sx={{ fontSize: 22 }} />
                  <span>Recurring</span>
                </Box>
              </ToggleButton>
              <ToggleButton value="ONDEMAND" aria-label="On Demand">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TouchAppIcon sx={{ fontSize: 22 }} />
                  <span>On Demand</span>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
            {isRecurring ? (
              <FormControl size="small" fullWidth required>
                <InputLabel>Invoice date (day of month)</InputLabel>
                <Select
                  value={editForm.invoice_date === "" || editForm.invoice_date == null ? "" : editForm.invoice_date}
                  label="Invoice date (day of month)"
                  onChange={(e) => setEditForm((p) => ({ ...p, invoice_date: e.target.value === "" ? "" : Number(e.target.value) }))}
                  displayEmpty
                  notched={!(editForm.invoice_date === "" || editForm.invoice_date == null)}
                >
                  <MenuItem value=""> </MenuItem>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <MenuItem key={day} value={day}>
                      {formatOrdinalDay(day)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" sx={{ color: "#64748b", fontStyle: "italic", py: 0.5 }}>
                Invoice date is not used for On Demand billing.
              </Typography>
            )}

            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151" }}>Sold-To</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField label="Name" required value={editForm.sold_to_name || ""} onChange={(e) => setEditForm((p) => ({ ...p, sold_to_name: e.target.value }))} onBlur={() => markTouched("sold_to_name")} size="small" fullWidth error={touched.sold_to_name && !editForm.sold_to_name?.trim()} />
              <TextField label="Attention (Attn)" value={editForm.sold_to_attn || ""} onChange={(e) => setEditForm((p) => ({ ...p, sold_to_attn: e.target.value }))} size="small" fullWidth />
              <Autocomplete
                size="small"
                fullWidth
                options={countryList}
                getOptionLabel={(option) => option?.name ?? ""}
                value={countryList.find((c) => c.name === (editForm.sold_to_country ?? "")) ?? null}
                onChange={(e, newValue) => {
                  setEditForm((p) => ({ ...p, sold_to_country: newValue?.name ?? "" }));
                  setSoldToAddressValidationStatus(null);
                  setSoldToAddressValidationMessage("");
                }}
                onBlur={() => markTouched("sold_to_country")}
                isOptionEqualToValue={(option, value) => option?.name === value?.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    required
                    error={touched.sold_to_country && !editForm.sold_to_country?.trim()}
                  />
                )}
              />
              <TextField label="Address line 1" required value={editForm.sold_to_address_line1 || ""} onChange={(e) => setEditForm((p) => ({ ...p, sold_to_address_line1: e.target.value }))} onBlur={() => { markTouched("sold_to_address_line1"); validateSoldToAddress(); }} size="small" fullWidth error={(touched.sold_to_address_line1 && !editForm.sold_to_address_line1?.trim()) || soldToAddressValidationStatus === "invalid"} InputProps={{ endAdornment: soldToAddressValidationStatus === "valid" ? <InputAdornment position="end"><CheckCircleIcon color="success" fontSize="small" /></InputAdornment> : null }} />
              {soldToAddressValidationMessage && <Typography variant="caption" sx={{ color: soldToAddressValidationStatus === "valid" ? "success.main" : soldToAddressValidationStatus === "invalid" ? "error.main" : "text.secondary" }}>{soldToAddressValidationMessage}</Typography>}
              <TextField label="Address line 2" value={editForm.sold_to_address_line2 || ""} onChange={(e) => setEditForm((p) => ({ ...p, sold_to_address_line2: e.target.value }))} size="small" fullWidth />
              <TextField label="City" required value={editForm.sold_to_city || ""} onChange={(e) => setEditForm((p) => ({ ...p, sold_to_city: e.target.value }))} onBlur={() => markTouched("sold_to_city")} size="small" fullWidth error={touched.sold_to_city && !editForm.sold_to_city?.trim()} />
              <TextField label="State" required value={editForm.sold_to_state || ""} onChange={(e) => setEditForm((p) => ({ ...p, sold_to_state: e.target.value }))} onBlur={() => markTouched("sold_to_state")} size="small" fullWidth error={touched.sold_to_state && !editForm.sold_to_state?.trim()} />
              <TextField label="Postal code" required value={editForm.sold_to_postal || ""} onChange={(e) => setEditForm((p) => ({ ...p, sold_to_postal: e.target.value }))} onBlur={() => markTouched("sold_to_postal")} size="small" fullWidth error={touched.sold_to_postal && !editForm.sold_to_postal?.trim()} />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151" }}>Ship-To (optional)</Typography>
              <Button size="small" variant="text" onClick={fillShipToFromSoldTo} sx={{ textTransform: "none", minWidth: 0 }} title="Copy Sold-To address">
                Same as Sold-To
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField label="Name" value={editForm.ship_to_name || ""} onChange={(e) => setEditForm((p) => ({ ...p, ship_to_name: e.target.value }))} size="small" fullWidth />
              <TextField label="Attention (Attn)" value={editForm.ship_to_attn || ""} onChange={(e) => setEditForm((p) => ({ ...p, ship_to_attn: e.target.value }))} size="small" fullWidth />
              <Autocomplete
                size="small"
                fullWidth
                options={countryList}
                getOptionLabel={(option) => option?.name ?? ""}
                value={countryList.find((c) => c.name === (editForm.ship_to_country ?? "")) ?? null}
                onChange={(e, newValue) => {
                  setEditForm((p) => ({ ...p, ship_to_country: newValue?.name ?? "" }));
                  setShipToAddressValidationStatus(null);
                  setShipToAddressValidationMessage("");
                }}
                onBlur={() => markTouched("ship_to_country")}
                isOptionEqualToValue={(option, value) => option?.name === value?.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    required
                    error={touched.ship_to_country && !editForm.ship_to_country?.trim()}
                  />
                )}
              />
              <TextField label="Address line 1" value={editForm.ship_to_address_line1 || ""} onChange={(e) => setEditForm((p) => ({ ...p, ship_to_address_line1: e.target.value }))} onBlur={() => validateShipToAddress()} size="small" fullWidth error={shipToAddressValidationStatus === "invalid"} InputProps={{ endAdornment: shipToAddressValidationStatus === "valid" ? <InputAdornment position="end"><CheckCircleIcon color="success" fontSize="small" /></InputAdornment> : null }} />
              {shipToAddressValidationMessage && <Typography variant="caption" sx={{ color: shipToAddressValidationStatus === "valid" ? "success.main" : shipToAddressValidationStatus === "invalid" ? "error.main" : "text.secondary" }}>{shipToAddressValidationMessage}</Typography>}
              <TextField label="Address line 2" value={editForm.ship_to_address_line2 || ""} onChange={(e) => setEditForm((p) => ({ ...p, ship_to_address_line2: e.target.value }))} size="small" fullWidth />
              <TextField label="City" value={editForm.ship_to_city || ""} onChange={(e) => setEditForm((p) => ({ ...p, ship_to_city: e.target.value }))} size="small" fullWidth />
              <TextField label="State" value={editForm.ship_to_state || ""} onChange={(e) => setEditForm((p) => ({ ...p, ship_to_state: e.target.value }))} size="small" fullWidth />
              <TextField label="Postal code" value={editForm.ship_to_postal || ""} onChange={(e) => setEditForm((p) => ({ ...p, ship_to_postal: e.target.value }))} size="small" fullWidth />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151" }}>Send To</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                Choose who receives invoices. One primary recipient is required; CC is optional (comma-separated).
              </Typography>
              <Autocomplete
                size="small"
                fullWidth
                options={invoiceSendToEmailOptions}
                value={(editForm.invoice_send_to_email ?? "").trim() || null}
                onChange={(_, value) => setEditForm((p) => ({ ...p, invoice_send_to_email: value ?? "" }))}
                onBlur={() => markTouched("invoice_send_to_email")}
                loading={customerEmailOptionsLoading}
                getOptionLabel={(option) => option}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Primary recipient"
                    required
                    error={touched.invoice_send_to_email && !(editForm.invoice_send_to_email ?? "").trim()}
                    helperText="Select the invoice recipient. Invoices will be sent to this address."
                  />
                )}
              />
              <Autocomplete
                size="small"
                fullWidth
                multiple
                options={ccEmailOptions}
                value={(editForm.invoice_cc_emails ?? "").split(",").map((e) => e.trim()).filter(Boolean)}
                onChange={(_, selected) => setEditForm((p) => ({ ...p, invoice_cc_emails: selected.join(", ") }))}
                loading={customerEmailOptionsLoading}
                getOptionLabel={(option) => option}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="CC (optional)"
                    placeholder="Add CC recipients"
                    helperText="Additional recipients receive a copy. Internal mail group is always BCCed."
                  />
                )}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={() => { setCustomerEditModalOpen(false); setEditingCustomer(null); }} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveCustomerProfile} disabled={isUpdatingProfile || !isCustomerFormValid} sx={{ textTransform: "none" }}>
            {isUpdatingProfile ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigureBillingProfile;
